import type {
  CreateWorkspaceUserDto,
  ResetPasswordRequest,
  UpdateWorkspaceUserDto,
  WorkspaceUserDto,
} from '@schwrz/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '../shared/api/client';
import { assignableUsersForClient } from '../shared/lib/assignableUsers';

export const useWorkspaceUsersStore = defineStore('workspaceUsers', () => {
  const items = ref<WorkspaceUserDto[]>([]);
  const loaded = ref(false);
  const loading = ref(false);

  const employees = computed(() => items.value.filter((u) => u.membershipType === 'employee'));
  const companyMembers = computed(() =>
    items.value.filter((u) => u.membershipType === 'company'),
  );

  async function load(force = false): Promise<void> {
    if (loaded.value && !force) return;
    loading.value = true;
    try {
      const res = await api<{ users: WorkspaceUserDto[] }>('/api/workspace-users');
      items.value = res.users;
      loaded.value = true;
    } finally {
      loading.value = false;
    }
  }

  function byId(id: string | null | undefined): WorkspaceUserDto | undefined {
    return id ? items.value.find((u) => u.id === id) : undefined;
  }

  function assignableForClient(clientId: string | null | undefined): WorkspaceUserDto[] {
    return assignableUsersForClient(items.value, clientId);
  }

  async function create(payload: CreateWorkspaceUserDto): Promise<WorkspaceUserDto> {
    const created = await api<WorkspaceUserDto>('/api/workspace-users', {
      method: 'POST',
      body: payload,
    });
    items.value = [...items.value, created];
    return created;
  }

  async function update(id: string, payload: UpdateWorkspaceUserDto): Promise<WorkspaceUserDto> {
    const updated = await api<WorkspaceUserDto>(`/api/workspace-users/${id}`, {
      method: 'PATCH',
      body: payload,
    });
    items.value = items.value.map((u) => (u.id === id ? updated : u));
    return updated;
  }

  async function remove(id: string): Promise<void> {
    await api<void>(`/api/workspace-users/${id}`, { method: 'DELETE' });
    items.value = items.value.filter((u) => u.id !== id);
  }

  async function resetPassword(id: string, payload: ResetPasswordRequest): Promise<void> {
    await api<{ ok: true }>(`/api/workspace-users/${id}/password`, {
      method: 'POST',
      body: payload,
    });
  }

  return {
    items,
    employees,
    companyMembers,
    loaded,
    loading,
    load,
    byId,
    assignableForClient,
    create,
    update,
    remove,
    resetPassword,
  };
});
